import { useEffect, useState } from 'react';
import { wrapNoticeText } from '@cornerstone/shared';
import { figmaTemplateTexts, FONT_PRETENDARD, FONT_SOLMOE, type FigmaTemplateText, type StationCFigmaTemplate } from './stationcFigmaTemplates';

// =============================================================================
// 템플릿 문구가 실제 화면(Player)에서 몇 줄이 되는지 — Player 캔버스와 같은 폰트·줄바꿈(wrapNoticeText)으로
// 디자인 좌표(2160×3840)에서 잰다. Player 는 칸 밖으로 넘친 줄을 자르지 않으므로 넘침 = 다른 글자와 겹침.
// 캔버스가 없는 환경(테스트)에서는 넘침을 판정하지 않고, AI 줄 예산은 글자 폭 근사로 계산한다.
// =============================================================================

const HANGUL_SAMPLE = '한림대학교신입생환영행사소식안내공지사항모집합니다';
let canvas: HTMLCanvasElement | undefined;
function contextFor(node: FigmaTemplateText): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  const ctx = (canvas ??= document.createElement('canvas')).getContext('2d');
  if (!ctx) return null;
  const family = node.fontFamily ? `'${node.fontFamily}', ` : '';
  ctx.font = `${node.fontWeight} ${node.fontSize}px ${family}'Inter Tight', 'Pretendard', sans-serif`;
  if ('letterSpacing' in ctx) (ctx as { letterSpacing: string }).letterSpacing = `${node.letterSpacing ?? 0}px`;
  return ctx;
}

/** 칸 높이에 들어가는 줄 수. */
export const boxLines = (node: FigmaTemplateText): number => Math.max(1, Math.floor(node.box[3] / (node.fontSize * node.lineHeight) + 1e-6));

/** 넘침 — Player 에서 칸 밖으로 흘러 다른 글자와 겹친다. 잴 수 없으면 false. */
export function overflows(node: FigmaTemplateText, value: string): boolean {
  const ctx = contextFor(node);
  return !!ctx && wrapNoticeText(value, node.box[2], (text) => ctx.measureText(text).width).length > boxLines(node);
}

/** 한 줄에 들어가는 한글 글자 수(소수 둘째 자리) — AI 줄 예산. 서버는 버림한 값을 프롬프트에, 소수를 넘침 어림에 쓴다. */
function hangulPerLine(node: FigmaTemplateText): number {
  const ctx = contextFor(node);
  const glyph = ctx ? ctx.measureText(HANGUL_SAMPLE).width / HANGUL_SAMPLE.length : node.fontSize * 0.864 + (node.letterSpacing ?? 0);
  return Math.max(1, Math.floor(node.box[2] / glyph * 100) / 100);
}

export type AiDraftField = { key: string; label: string; text: string; maxLength: number; role: string; lines: number; perLine: number;
  fact?: true; keep?: true; today?: true };
/** AI 초안 요청의 칸 목록 — 템플릿별 튜닝(ai)과 실측 줄 예산. 시안이 박스보다 적은 줄을 쓰면 시안을 따른다. */
export function aiDraftFields(template: StationCFigmaTemplate): AiDraftField[] {
  return figmaTemplateTexts(template).map((node) => {
    const lines = Math.min(node.ai?.lines ?? boxLines(node), boxLines(node)), perLine = hangulPerLine(node);
    return { key: node.id, label: node.label, text: node.text, role: node.ai?.role ?? node.label, lines, perLine,
      maxLength: Math.min(1000, Math.max(8, Math.floor(lines * perLine * 2))),
      ...(node.ai?.fact ? { fact: true as const } : {}), ...(node.ai?.keep ? { keep: true as const } : {}), ...(node.ai?.today ? { today: true as const } : {}) };
  });
}

/** 템플릿 글꼴을 내려받은 뒤 한 번 다시 그린다 — 그 전 측정은 대체 글꼴 기준이다. */
export function useTemplateFontsReady(): boolean {
  const [ready, setReady] = useState(() => typeof document === 'undefined' || !document.fonts);
  useEffect(() => {
    if (ready) return;
    let alive = true;
    void Promise.all([`800 100px '${FONT_PRETENDARD}'`, `500 100px '${FONT_SOLMOE}'`, `300 100px '${FONT_SOLMOE}'`].map((font) => document.fonts.load(font)))
      .catch(() => undefined).then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [ready]);
  return ready;
}
